import type { BrandCoAgentInput } from "@/config/brandCoAgentPrompts";
import type { BrandResearchQueryPlan } from "@/features/department";
import type {
  BrandConfig,
  BrandPopulateResult,
  PerplexityResearchResult,
  ResearchSynthesis,
  TwitterResearchResult,
  YoutubeResearchResult,
} from "@/config/brand";
import {
  buildDraftBrandConfigFromCampaignData,
  type DraftBrandConfigCampaignData,
} from "@/features/brand/lib/brandConfig";

export interface BrandCampaignDataLike extends DraftBrandConfigCampaignData {
  brandConfig?: BrandConfig;
  brandResearchStatus?: "idle" | "running" | "complete" | "error";
  brandResearchPhase?: string;
  brandResearchProgress?: { phase: string; completed: number; total: number };
  brandCollectionTrackProgress?: Record<string, { completed: number; total: number }>;
  brandQueryPlan?: BrandResearchQueryPlan;
  brandTwitterResult?: TwitterResearchResult;
  brandPerplexityResult?: PerplexityResearchResult;
  brandYoutubeResult?: YoutubeResearchResult;
  brandGoogleSearchResult?: PerplexityResearchResult;
  brandSynthesis?: ResearchSynthesis;
  brandPopulateResult?: BrandPopulateResult;
  brandCoAgentInput?: BrandCoAgentInput;
  brandSavedId?: string;
  brandLogoGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandIconManifestStatus?: "idle" | "generating" | "complete" | "error";
  brandIconGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandAccessoryGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandBackgroundGenerationStatus?: "idle" | "generating" | "complete" | "error";
  [key: string]: unknown;
}

const BRAND_SYNTHESIS_FIELDS = new Set([
  "brandTagline",
  "uniqueValueProposition",
  "targetAudience",
  "brandPersonality",
  "suggestedTone",
  "competitiveAdvantage",
  "contentStrategy",
]);

const BRAND_ROOT_FIELDS = new Set([
  "brandPopulateResult",
  "brandSynthesis",
  "brandCoAgentInput",
  "brandLogoOptions",
  "brandLogoSelectedIndex",
  "brandIconManifest",
  "brandIconResults",
  "brandAccessoryAssets",
  "brandBackgroundAssets",
  "brandSavedId",
  // NOTE: 'brandConfig' is intentionally excluded — it is a derived/computed field
  // rebuilt by syncBrandConfigInCampaignData. Direct writes via update_brand_field
  // would be silently overwritten, so brandConfig.* paths are rejected in buildBrandFieldPatch.
]);

const BRAND_CAMPAIGN_KEYS = new Set([
  "brandCoAgentInput",
  "brandWebsiteContent",
  "brandQueryPlan",
  "brandResearchStatus",
  "brandResearchPhase",
  "brandResearchProgress",
  "brandCollectionTrackProgress",
  "brandTwitterResult",
  "brandPerplexityResult",
  "brandYoutubeResult",
  "brandGoogleSearchResult",
  "brandSynthesis",
  "brandPopulateResult",
  "brandConfig",
  "brandSavedId",
  "brandLogoOptions",
  "brandLogoSelectedIndex",
  "brandLogoGenerationStatus",
  "brandIconManifest",
  "brandIconManifestStatus",
  "brandIconResults",
  "brandIconGenerationStatus",
  "brandAccessoryAssets",
  "brandAccessoryGenerationStatus",
  "brandBackgroundAssets",
  "brandBackgroundGenerationStatus",
]);

function isArrayIndexSegment(segment: string): boolean {
  return /^\d+$/.test(segment);
}

function setAtPath(current: unknown, pathSegments: string[], value: unknown): unknown {
  if (pathSegments.length === 0) return value;
  const [head, ...rest] = pathSegments;
  const key = isArrayIndexSegment(head) ? Number(head) : head;

  const base = current ?? (typeof key === "number" ? [] : {});
  const copy = Array.isArray(base) ? [...base] : { ...(base as Record<string, unknown>) };

  const prevChild = (base as Record<string | number, unknown>)?.[key];
  (copy as Record<string | number, unknown>)[key] =
    rest.length > 0 ? setAtPath(prevChild, rest, value) : value;
  return copy;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function resolveLogoSelectionIndex(
  payload: Record<string, unknown>,
  logos: Array<{ aesthetic?: string; label?: string }>,
): number | null {
  const index = Number(payload.index);
  if (Number.isInteger(index) && index >= 0 && index < logos.length) {
    return index;
  }

  const aesthetic =
    typeof payload.aesthetic === "string" ? payload.aesthetic.trim().toLowerCase() : "";
  if (aesthetic) {
    const byAesthetic = logos.findIndex(
      (logo) => (logo.aesthetic ?? "").toLowerCase() === aesthetic,
    );
    if (byAesthetic >= 0) return byAesthetic;
  }

  const label = typeof payload.label === "string" ? payload.label.trim().toLowerCase() : "";
  if (label) {
    const byLabel = logos.findIndex((logo) => (logo.label ?? "").toLowerCase() === label);
    if (byLabel >= 0) return byLabel;
  }

  return null;
}

export function containsBrandCampaignDataPatch(data: Record<string, unknown>): boolean {
  return Object.keys(data).some(
    (key) => BRAND_CAMPAIGN_KEYS.has(key) || BRAND_SYNTHESIS_FIELDS.has(key),
  );
}

export function buildBrandFieldPatch<T extends BrandCampaignDataLike>(
  campaignData: T,
  rawPath: string,
  value: unknown,
): Partial<T> | null {
  const path = rawPath.trim();
  if (!path) return null;

  const segments = path
    .split(".")
    .map((segment) => segment.trim())
    .filter(Boolean);
  if (segments.length === 0) return null;

  const [first, ...rest] = segments;

  // brandConfig is a derived/computed field — reject direct writes.
  // The agent should use visuals.*, brandSynthesis.*, etc. which update the
  // underlying source data; brandConfig is then rebuilt automatically.
  if (first === "brandConfig") return null;
  const rootKey = BRAND_ROOT_FIELDS.has(first)
    ? first
    : BRAND_SYNTHESIS_FIELDS.has(first)
      ? "brandSynthesis"
      : "brandPopulateResult";

  const nestedPath = BRAND_ROOT_FIELDS.has(first) ? rest : segments;
  const currentRoot = campaignData[rootKey];
  const nextRoot = nestedPath.length > 0 ? setAtPath(currentRoot, nestedPath, value) : value;
  return { [rootKey]: nextRoot } as Partial<T>;
}

export function syncBrandConfigInCampaignData<T extends BrandCampaignDataLike>(
  campaignData: T,
  sessionId?: string | null,
): T {
  const brandConfig = buildDraftBrandConfigFromCampaignData(campaignData, sessionId ?? undefined);
  if (!brandConfig) return campaignData;
  return {
    ...campaignData,
    brandConfig,
  };
}

export function applyBrandToolResultToCampaignData<T extends BrandCampaignDataLike>(
  campaignData: T,
  toolResult: unknown,
  sessionId?: string | null,
): T {
  const result = asRecord(toolResult);

  if (result.campaignData && typeof result.campaignData === "object") {
    return syncBrandConfigInCampaignData(
      { ...campaignData, ...(result.campaignData as Record<string, unknown>) } as T,
      sessionId,
    );
  }

  const action = typeof result.action === "string" ? result.action : "";
  if (!action) {
    return syncBrandConfigInCampaignData(campaignData, sessionId);
  }

  if (action === "update_brand_field") {
    const path = typeof result.path === "string" ? result.path : "";
    const patch = buildBrandFieldPatch(campaignData, path, result.value);
    return patch
      ? syncBrandConfigInCampaignData({ ...campaignData, ...patch } as T, sessionId)
      : syncBrandConfigInCampaignData(campaignData, sessionId);
  }

  if (action === "update_campaign_data") {
    const updates =
      result.updates && typeof result.updates === "object"
        ? (result.updates as Record<string, unknown>)
        : null;
    return updates
      ? syncBrandConfigInCampaignData({ ...campaignData, ...updates } as T, sessionId)
      : syncBrandConfigInCampaignData(campaignData, sessionId);
  }

  if (action === "select_brand_logo") {
    const logos = Array.isArray(campaignData.brandLogoOptions)
      ? campaignData.brandLogoOptions.map((logo) => asRecord(logo))
      : [];
    const selectedIndex = resolveLogoSelectionIndex(result, logos);
    if (selectedIndex === null) {
      return syncBrandConfigInCampaignData(campaignData, sessionId);
    }
    return syncBrandConfigInCampaignData(
      { ...campaignData, brandLogoSelectedIndex: selectedIndex } as T,
      sessionId,
    );
  }

  return syncBrandConfigInCampaignData(campaignData, sessionId);
}
